import React, { useState, useEffect } from 'react';
import { getAuth } from 'firebase/auth';
import { FaBook, FaCheckCircle, FaArrowRight } from 'react-icons/fa';
import { getGAClientId } from '../utils/GA4Analytics';

export default function EbookRecommendSlide({ onContinue, testType }) {
  const [isProcessing, setIsProcessing] = useState(false);
  const auth = getAuth();
  const user = auth.currentUser;
  const [currency, setCurrency] = useState('INR');

  useEffect(() => {
    const getDetectedCurrency = () => {
      const country = sessionStorage.getItem('ga4_user_country');
      const browserLang = navigator.language || '';
      const sessionLocale = sessionStorage.getItem('versant_locale');
      
      if (window.location.pathname.startsWith('/es') || country === 'Spain' || browserLang.startsWith('es') || sessionLocale === 'es') {
        return 'EUR';
      }
      const highPayingUSDCounteries = ['United States', 'United Kingdom', 'Canada', 'Japan', 'Singapore', 'Australia', 'Brazil', 'South Korea'];
      if (highPayingUSDCounteries.includes(country)) {
        return 'USD';
      }
      if (country === 'India') {
        return 'INR';
      }
      return 'USD';
    };
    setCurrency(getDetectedCurrency());
  }, []);

  // Load Razorpay SDK
  const loadRazorpay = () => {
    return new Promise((resolve) => {
      if (window.Razorpay) {
        resolve(true);
        return;
      }
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  const handleBuyNow = async () => {
    setIsProcessing(true);
    const razorpayLoaded = await loadRazorpay();

    if (!razorpayLoaded) {
      alert('Razorpay failed to load. Please check your internet connection.');
      setIsProcessing(false);
      return;
    }

    try {
      const checkoutEmail = user ? user.email : '';
      const checkoutUid = user ? user.uid : 'guest';

      const orderResponse = await fetch('/.netlify/functions/createRazorpayOrder', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          uid: checkoutUid,
          email: checkoutEmail,
          productType: 'ebook',
          currency: currency,
          referredBy: localStorage.getItem('versant_affiliate_ref') || null,
          gaClientId: getGAClientId()
        })
      });

      if (!orderResponse.ok) {
        throw new Error("Failed to create checkout order on server.");
      }

      const orderData = await orderResponse.json();

      const options = {
        key: process.env.REACT_APP_RAZORPAY_KEY_ID,
        amount: orderData.amount,
        currency: orderData.currency,
        name: "Versant Test Mastery Ebook",
        description: "Complete preparation guide with 20 practice sets",
        image: "https://versantpro.com/logo.png",
        order_id: orderData.order_id,
        prefill: {
          email: checkoutEmail,
          name: user ? (user.displayName || '') : ''
        },
        theme: {
          color: "#0f766e" // Teal theme
        },
        handler: async function (response) {
          setIsProcessing(true);
          console.log("Payment Success! Verifying...");
          try {
            const verifyRes = await fetch('/.netlify/functions/verifyRazorpayPayment', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
                uid: checkoutUid,
                email: checkoutEmail,
                productType: 'ebook',
                referredBy: localStorage.getItem('versant_affiliate_ref') || null
              })
            });

            if (verifyRes.ok) {
              // Set local purchase flag so recommendation is skipped
              localStorage.setItem('ebook_purchased', 'true');
              
              // Redirect to thank you page with test return param
              window.location.href = `/thank-you?payment_id=${response.razorpay_payment_id}&redirect_to_test=${testType}`;
            } else {
              let errMsg = "Unknown server error.";
              try {
                const errorData = await verifyRes.json();
                errMsg = errorData.error || errMsg;
              } catch (e) {
                errMsg = `Server returned status code ${verifyRes.status}`;
              }
              alert("Verification error: " + errMsg);
            }
          } catch (verifyError) {
            console.error("Signature verification query failed:", verifyError);
            alert("Verification check failed. If amount was deducted, please check your email or contact support.");
          } finally {
            setIsProcessing(false);
          }
        }
      };

      const paymentObject = new window.Razorpay(options);
      paymentObject.open();

    } catch (err) {
      console.error("Payment initialization failed:", err);
      alert("Could not initialize transaction. Please try again later.");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleContinue = () => {
    // Dismiss recommendation card for today (24h skip)
    const todayStr = new Date().toDateString();
    localStorage.setItem('ebook_recommend_dismissed_date', todayStr);
    onContinue();
  };

  return (
    <div className="min-h-screen bg-slate-900 bg-gradient-to-br from-slate-950 via-emerald-950 to-slate-900 flex flex-col items-center justify-center p-4 sm:p-6 font-sans text-gray-800 relative overflow-hidden select-none">
      {/* Dynamic Glow effects */}
      <div className="absolute top-[-10%] left-[-10%] w-[50%] h-[50%] bg-emerald-500/15 blur-[140px] rounded-full pointer-events-none"></div>
      <div className="absolute bottom-[-10%] right-[-10%] w-[50%] h-[50%] bg-teal-500/15 blur-[140px] rounded-full pointer-events-none"></div>

      <div className="bg-gradient-to-br from-emerald-900/90 via-slate-900/95 to-teal-950/90 border border-emerald-500/30 backdrop-blur-2xl shadow-[0_0_60px_rgba(16,185,129,0.2)] rounded-[2.5rem] p-6 sm:p-10 w-full max-w-4xl text-left relative z-10 animate-fade-in flex flex-col md:flex-row items-center gap-8 md:gap-10 transition-all duration-500">
        
        {/* Left Column: 3D Ebook Mockup */}
        <div className="w-full md:w-5/12 flex flex-col items-center justify-center relative">
          <div className="relative group">
            {/* Ambient Image Shadow/Glow */}
            <div className="absolute -inset-2 bg-gradient-to-r from-emerald-500 to-teal-400 rounded-2xl blur-xl opacity-40 group-hover:opacity-75 transition duration-500"></div>
            
            {/* 3D Realistic Book Container */}
            <div className="relative rounded-2xl overflow-hidden shadow-[0_20px_50px_rgba(0,0,0,0.5)] border border-white/20 transform transition-all duration-500 hover:scale-[1.03] hover:rotate-1">
              <img 
                src="/images/versant_ebook_cover.jpg" 
                alt="Versant Test Mastery eBook Cover" 
                className="w-48 sm:w-60 md:w-full max-w-[260px] h-auto object-cover rounded-xl"
              />
              {/* Badge overlay on cover */}
              <div className="absolute top-3 right-3 bg-gradient-to-r from-amber-400 to-orange-500 text-slate-950 font-black text-[10px] uppercase tracking-wider px-2.5 py-1 rounded-full shadow-lg">
                ★ 20 Practice Sets
              </div>
            </div>
          </div>

          <div className="mt-4 flex items-center gap-2 text-emerald-300 text-xs font-semibold">
            <span>⚡ Instant PDF Access After Checkout</span>
          </div>
        </div>

        {/* Right Column: High-Converting Copy & Actions */}
        <div className="w-full md:w-7/12 flex flex-col items-start">
          {/* Header Tag */}
          <span className="px-3.5 py-1 bg-emerald-500/20 border border-emerald-400/40 rounded-full text-emerald-300 text-xs font-black uppercase tracking-widest mb-4 flex items-center gap-1.5">
            <span>💡 SMART RECOMMENDATION</span>
          </span>

          <h2 className="text-2xl sm:text-4xl font-black text-white mb-2 tracking-tight leading-tight">
            Versant Test Mastery Guide
          </h2>

          <p className="text-emerald-100/80 text-sm sm:text-base mb-6 font-light leading-relaxed">
            Crack the automated AI voice & scoring algorithms before you start! Get 20 full practice sets, high-scoring speaking templates, and essential listening drills.
          </p>

          {/* Pricing Highlight */}
          <div className="flex items-center gap-3 mb-6 bg-white/10 border border-white/15 px-5 py-2.5 rounded-2xl w-full justify-between sm:justify-start">
            <span className="text-emerald-300/60 line-through text-base font-medium">
              {currency === 'INR' ? '₹499' : currency === 'EUR' ? '€7.99' : '$7.99'}
            </span>
            <span className="text-3xl font-black text-white drop-shadow-[0_2px_8px_rgba(0,0,0,0.3)]">
              {currency === 'INR' ? '₹199' : currency === 'EUR' ? '€2.99' : '$2.99'}
            </span>
            <span className="text-emerald-300 bg-emerald-500/20 border border-emerald-400/30 text-[10px] font-black uppercase tracking-wider px-2.5 py-1 rounded-full">
              60% OFF TODAY
            </span>
          </div>

          {/* Features Checklist */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 w-full mb-8 text-left border-t border-white/10 pt-4">
            {[
              "20 Full Practice Drills",
              "Repeat Sentence Hacks",
              "Story Retelling Outlines",
              "High-Scoring Speech Prompts",
              "Passage Reconstruction Rules",
              "Instant 24/7 Download"
            ].map((feat, idx) => (
              <div key={idx} className="flex items-center gap-2.5 text-white font-medium text-xs sm:text-sm">
                <FaCheckCircle className="text-emerald-400 flex-shrink-0 text-xs" />
                <span>{feat}</span>
              </div>
            ))}
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-3 items-center justify-start w-full">
            <button
              onClick={handleBuyNow}
              disabled={isProcessing}
              className="px-6 py-3.5 rounded-xl font-black text-slate-950 bg-gradient-to-r from-emerald-400 to-teal-300 hover:from-emerald-300 hover:to-teal-200 transition-all w-full sm:w-auto flex-1 flex items-center justify-center gap-2 text-sm sm:text-base shadow-[0_0_25px_rgba(16,185,129,0.4)] hover:shadow-[0_0_35px_rgba(16,185,129,0.6)] transform hover:-translate-y-0.5 active:scale-95 disabled:opacity-50"
            >
              {isProcessing ? "Processing..." : "Get eBook Now"} <FaArrowRight />
            </button>
            <button
              onClick={handleContinue}
              className="px-6 py-3.5 rounded-xl font-bold text-slate-300 bg-white/5 border border-white/20 hover:bg-white/10 transition-all w-full sm:w-auto flex-1 text-sm sm:text-base text-center"
            >
              Skip & Start Free Test
            </button>
          </div>
        </div>

      </div>
    </div>
  );
}
